/**
 * Typed wrapper for supabase.functions.invoke failures.
 *
 * supabase-js surfaces non-2xx edge function responses as a FunctionsHttpError
 * whose `context` is the raw Response — the JSON body (where our functions put
 * machine-readable codes like 'slug_taken') has to be read asynchronously.
 * Services normalize that into an EdgeFunctionError so UI code can branch on
 * `code`/`status` without knowing about Response objects.
 */
export class EdgeFunctionError extends Error {
  code?: string
  status?: number

  constructor(message: string, opts: { code?: string; status?: number } = {}) {
    super(message)
    this.name = "EdgeFunctionError"
    this.code = opts.code
    this.status = opts.status
  }
}

interface ResponseLike {
  status?: number
  json?: () => Promise<unknown>
}

export async function toEdgeFunctionError(error: unknown): Promise<EdgeFunctionError> {
  const message = error instanceof Error ? error.message : "Edge function call failed"
  const context = (error as { context?: ResponseLike } | null)?.context

  let code: string | undefined
  let status: number | undefined
  if (context) {
    status = context.status
    if (typeof context.json === "function") {
      try {
        const body = (await context.json()) as { error?: string } | null
        if (body?.error) code = body.error
      } catch {
        // Body wasn't JSON — keep the generic message.
      }
    }
  }

  return new EdgeFunctionError(code ?? message, { code, status })
}
